import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { injectIntl } from "react-intl";
import Typography from "@material-ui/core/Typography";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import Checkbox from "@material-ui/core/Checkbox";
import Tooltip from "@material-ui/core/Tooltip";
import Button from "@material-ui/core/Button";
import Divider from "@material-ui/core/Divider";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import { DataGridItem } from "components/data-grid";
import { StyledTextField } from "components/form/form-textfield";
import { SimpleCheckbox } from "components/form/form-checkbox";
import Select from "components/select";
import IconButton from "components/icon-button";
import withStyles from "components/with-styles";
import styles from "./styles/StreamSourcesSection.css.js";

const StreamSourcesSection = ({
  intl,
  allStreamSources,
  values,
  errors,
  selectedStreamSources,
  setSelectedStreamSources,
  camOpSourceIndex,
  expandedAccordions,
  setExpandedAccordions,
  handleStreamSourceSelectionChange,
  handleStreamSourceLabelChange,
  handleCamOpChange,
  handleAddAudioSource,
  handleRemoveAudioSource,
  audioSourceLinks,
  handlePreselectedChange,
  handleMixerVideoChange,
  handleStreamSourceTypeChange,
  handleOverlayFieldChange,
  handleSteerableFieldChange,
  handleAddUrlParameter,
  getUrlParameters,
  handleUrlParameterChange,
  handleRemoveUrlParameter,
  setAllStreamSources,
  setValue,
  sourceFormData,
  handleAutoExposureFieldChange,
  handleUseAutoExposureChange,
  classes,
}) => {
  // Auto-expand accordion when there's a validation error on any stream source
  useEffect(() => {
    if (errors?.file?.stream_sources && values?.file?.stream_sources) {
      const streamSourceErrors = errors.file.stream_sources;
      const formStreamSources = values.file.stream_sources;

      // Match errors to allStreamSources by comparing with form data
      formStreamSources.forEach((formSource, formIndex) => {
        // Check if this source has any validation errors
        const hasError = streamSourceErrors[formIndex] && Object.keys(streamSourceErrors[formIndex]).length > 0;
        
        if (hasError) {
          // Find the corresponding index in allStreamSources by matching cam_id or URL
          const allSourcesIndex = allStreamSources.findIndex(s => 
            s.cam_id === formSource.cam_id || 
            s.url === formSource.url ||
            s.label === formSource.label
          );
          
          if (allSourcesIndex !== -1) {
            setExpandedAccordions(prev => {
              if (!prev[allSourcesIndex]) {
                return { ...prev, [allSourcesIndex]: true };
              }
              return prev;
            });
          }
        }
      });
    }
  }, [errors?.file?.stream_sources, values?.file?.stream_sources, allStreamSources, setExpandedAccordions]);

  const handleAddVptzCamera = () => {
    const vptzCount = allStreamSources.filter(s => s.label?.startsWith("vptz")).length;
    const newVptzSource = {
      label: `vptz${vptzCount > 0 ? vptzCount + 1 : ""}`,
      cam_id: `vptz${vptzCount > 0 ? vptzCount + 1 : ""}`,
      proto: "rtsp",
      url: "vcam://PAN/?bitrate=6000000&width=1920&height=1080&pan=0&tilt=0&fov=90&dewarp_id=",
      description: "Virtual PTZ Camera",
    };

    const newAllStreamSources = [...allStreamSources, newVptzSource];
    setAllStreamSources(newAllStreamSources);

    const newVptzIndex = allStreamSources.length;
    setSelectedStreamSources([...selectedStreamSources, newVptzIndex]);

    // Expand the accordion for the newly added vptz camera
    setExpandedAccordions(prev => ({ ...prev, [newVptzIndex]: true }));

    const currentFormSources = values.file?.stream_sources || [];
    const { description, ...sourceWithoutDescription } = newVptzSource;
    const newFormSource = {
      ...sourceWithoutDescription,
      url: "vcam://PAN/?bitrate=6000000&width=1920&height=1080&pan=0&tilt=0&fov=90&dewarp_id=", // Explicitly set URL
    };

    const updatedFormSources = [...currentFormSources, newFormSource];
    setValue("file.stream_sources", updatedFormSources);
  };

  const handleDeleteVptzCamera = index => () => {
    const streamSource = allStreamSources[index];
    if (streamSource.label?.startsWith("vptz")) {
      const newAllStreamSources = allStreamSources.filter((_, i) => i !== index);
      setAllStreamSources(newAllStreamSources);

      const newSelectedStreamSources = selectedStreamSources.filter(i => i !== index).map(i => (i > index ? i - 1 : i));
      setSelectedStreamSources(newSelectedStreamSources);

      const newStreamSources = values.file?.stream_sources?.filter(source => source.label !== streamSource.label) || [];
      setValue("file.stream_sources", newStreamSources);
    }
  };

  return (
    <>
      <DataGridItem big>
        <div className={classes.headerContainer}>
          <Typography variant="h6" gutterBottom className={classes.title}>
            {intl.formatMessage({ id: "roomTemplate.streamSources.title" }, { defaultMessage: "Stream Sources" })}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddVptzCamera}
            disabled={!values.file?.virtualcam}
          >
            {intl.formatMessage({ id: "roomTemplate.streamSources.addVptz" }, { defaultMessage: "Add Virtual Camera" })}
          </Button>
        </div>
        <Typography variant="caption" color="textSecondary" className={classes.instructions}>
          {intl.formatMessage(
            { id: "roomTemplate.streamSources.instructions" },
            { defaultMessage: "Use checkbox to select sources, radio button to set Camera Operator (camOp)" },
          )}
        </Typography>
        {errors?.file?.stream_sources && (
          <Typography variant="caption" color="error" style={{ display: "block", marginTop: "8px" }}>
            {errors.file.stream_sources.message}
          </Typography>
        )}
        {allStreamSources && allStreamSources.length > 0 ? (
          allStreamSources.map((streamSource, index) => {
            const hasCamOp = camOpSourceIndex === index;
            const selectedIndex = selectedStreamSources.indexOf(index);
            const formSource = selectedIndex !== -1 ? values.file?.stream_sources?.[selectedIndex] : null;
            const isVptz = streamSource.label?.startsWith("vptz");
            const isChecked = selectedStreamSources.includes(index);
            const isAudioSource = streamSource.isAudioSource || false;

            return (
              <Accordion
                key={index}
                className={isAudioSource ? classes.audioAccordion : classes.accordion}
                expanded={expandedAccordions[index] || false}
                onChange={(e, isExpanded) => {
                  setExpandedAccordions(prev => ({ ...prev, [index]: isExpanded }));
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`stream-source-${index}-content`}
                  id={`stream-source-${index}-header`}
                >
                  <div className={classes.accordionSummary}>
                    <Checkbox
                      checked={isChecked}
                      onChange={handleStreamSourceSelectionChange(index)}
                      onClick={e => e.stopPropagation()}
                      color="primary"
                    />
                    <div className={classes.labelContainer}>
                      <StyledTextField
                        type="text"
                        value={formSource?.label || sourceFormData[index]?.label || streamSource.label || ""}
                        onChange={handleStreamSourceLabelChange(index)}
                        onClick={e => e.stopPropagation()}
                        label={intl.formatMessage(
                          { id: "roomTemplate.streamSources.label" },
                          { defaultMessage: "Label" },
                        )}
                        disabled={hasCamOp}
                        error={!hasCamOp && !!errors?.file?.stream_sources?.[selectedIndex]?.label}
                        helperText={
                          hasCamOp
                            ? intl.formatMessage(
                                { id: "roomTemplate.streamSources.labelDisabled" },
                                { defaultMessage: "Label is set to 'PAN' when CamOp is enabled" },
                              )
                            : errors?.file?.stream_sources?.[selectedIndex]?.label?.message || ""
                        }
                        fullWidth
                        className={classes.labelInput}
                      />
                      <Typography variant="caption" color="textSecondary" className={classes.description}>
                        {`${streamSource.brand || ""} ${streamSource.model || ""}`.trim()
                          ? `${streamSource.brand || ""} ${streamSource.model || ""}`.trim() +
                            (streamSource.description ? ` - ${streamSource.description}` : "")
                          : streamSource.description || ""}
                      </Typography>
                    </div>
                    <div className={classes.controlsContainer}>
                      {!isVptz && !isAudioSource && (
                        <>
                          <div className={classes.controlGroup}>
                            <Typography variant="caption" color="textSecondary">
                              {intl.formatMessage(
                                { id: "roomTemplate.streamSources.camOp" },
                                { defaultMessage: "camOp" },
                              )}
                            </Typography>
                            <Tooltip
                              title={
                                !selectedStreamSources.includes(index)
                                  ? intl.formatMessage(
                                      { id: "roomTemplate.streamSources.camOpDisabled" },
                                      { defaultMessage: "Select the source first to enable CamOp" },
                                    )
                                  : ""
                              }
                              placement="top"
                            >
                              <span>
                                <Checkbox
                                  checked={camOpSourceIndex === index}
                                  onChange={handleCamOpChange(index)}
                                  onClick={e => e.stopPropagation()}
                                  color="primary"
                                  disabled={!selectedStreamSources.includes(index)}
                                />
                              </span>
                            </Tooltip>
                          </div>
                          {!audioSourceLinks.has(index) && (
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={e => {
                                e.stopPropagation();
                                handleAddAudioSource(index)();
                              }}
                              className={classes.addAudioButton}
                            >
                              {intl.formatMessage(
                                { id: "roomTemplate.streamSources.addAudio" },
                                { defaultMessage: "Add Audio" },
                              )}
                            </Button>
                          )}
                        </>
                      )}
                      <div className={classes.controlGroup}>
                        <Typography variant="caption" color="textSecondary">
                          {intl.formatMessage(
                            { id: "roomTemplate.streamSources.addToMixer" },
                            { defaultMessage: "Add to mixer" },
                          )}
                        </Typography>
                        <Tooltip
                          title={
                            !selectedStreamSources.includes(index)
                              ? intl.formatMessage(
                                  { id: "roomTemplate.streamSources.addToMixerDisabled" },
                                  { defaultMessage: "Select the source first to add it to the mixer" },
                                )
                              : !formSource?.label
                              ? intl.formatMessage(
                                  { id: "roomTemplate.streamSources.addToMixerDisabledNoLabel" },
                                  { defaultMessage: "Add to mixer requires a label" },
                                )
                              : ""
                          }
                          placement="top"
                        >
                          <span>
                            <Checkbox
                              checked={(() => {
                                const label = formSource?.label;
                                if (!label) return false;
                                if (isAudioSource) {
                                  return (values.file?.mixer?.audio || []).includes(label);
                                }
                                return (values.file?.mixer?.video || []).includes(label);
                              })()}
                              onChange={handleMixerVideoChange(index)}
                              onClick={e => e.stopPropagation()}
                              color="primary"
                              disabled={!selectedStreamSources.includes(index) || !formSource?.label}
                            />
                          </span>
                        </Tooltip>
                      </div>
                      {!isAudioSource && (
                        <div className={classes.controlGroup}>
                          <Typography variant="caption" color="textSecondary">
                            {intl.formatMessage(
                              { id: "roomTemplate.streamSources.preselected" },
                              { defaultMessage: "Preselected" },
                            )}
                          </Typography>
                          <Tooltip
                            title={
                              hasCamOp
                                ? intl.formatMessage(
                                    { id: "roomTemplate.streamSources.preselectedDisabledCamOp" },
                                    { defaultMessage: "CamOp sources cannot be preselected" },
                                  )
                                : !formSource?.label
                                ? intl.formatMessage(
                                    { id: "roomTemplate.streamSources.preselectedDisabled" },
                                    { defaultMessage: "Preselected requires a label" },
                                  )
                                : !(values.file?.mixer?.video || []).includes(formSource?.label)
                                ? intl.formatMessage(
                                    { id: "roomTemplate.streamSources.preselectedDisabledNotInMixer" },
                                    { defaultMessage: "Source must be added to mixer first" },
                                  )
                                : ""
                            }
                            placement="top"
                          >
                            <span>
                              <Checkbox
                                checked={(() => {
                                  const label = formSource?.label;
                                  return label ? (values.file?.mixer?.preselected || []).includes(label) : false;
                                })()}
                                onChange={handlePreselectedChange(index)}
                                onClick={e => e.stopPropagation()}
                                color="primary"
                                disabled={
                                  !selectedStreamSources.includes(index) ||
                                  !formSource?.label ||
                                  hasCamOp ||
                                  !(values.file?.mixer?.video || []).includes(formSource?.label)
                                }
                              />
                            </span>
                          </Tooltip>
                        </div>
                      )}
                      {isVptz && (
                        <IconButton
                          color="secondary"
                          onClick={e => {
                            e.stopPropagation();
                            handleDeleteVptzCamera(index)();
                          }}
                          size="small"
                          aria-label="delete vptz camera"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                      {isAudioSource && (
                        <IconButton
                          color="secondary"
                          onClick={e => {
                            e.stopPropagation();
                            handleRemoveAudioSource(index)();
                          }}
                          size="small"
                          aria-label="delete audio source"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </div>
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <div className={classes.accordionDetails}>
                    {!isVptz && (
                      <div className={classes.formGrid}>
                        <Select
                          id={`stream-source-type-${index}`}
                          name={`stream-source-type-${index}`}
                          label={intl.formatMessage(
                            { id: "roomTemplate.streamSources.type" },
                            { defaultMessage: "Type" },
                          )}
                          value={
                            formSource?.type === "overlay"
                              ? "overlay"
                              : formSource?.type === ""
                              ? ""
                              : "auto"
                          }
                          onChange={handleStreamSourceTypeChange(index)}
                          elements={[
                            // Only shown while the loaded template still has an empty "type".
                            // It disappears as soon as another option is picked, and is never
                            // offered for sources that never had the field (new or form-created).
                            ...(formSource?.type === ""
                              ? [
                                  {
                                    value: "",
                                    label: intl.formatMessage(
                                      { id: "roomTemplate.streamSources.type.empty" },
                                      { defaultMessage: "Empty (invalid unless test template)" },
                                    ),
                                  },
                                ]
                              : []),
                            {
                              value: "auto",
                              label: intl.formatMessage(
                                { id: "roomTemplate.streamSources.type.stream" },
                                { defaultMessage: "Stream (por defecto)" },
                              ),
                            },
                            { value: "overlay", label: "Overlay" },
                          ]}
                          getValueFromElement={el => el.value}
                          getLabelFromElement={el => el.label}
                          helperText={errors?.file?.stream_sources?.[selectedIndex]?.type?.message || ""}
                        />
                      </div>
                    )}
                    {!isVptz && isChecked && formSource?.type === "overlay" && (
                      <div className={classes.overlayFieldsContainer}>
                        <Typography variant="subtitle2" gutterBottom>
                          {intl.formatMessage(
                            { id: "roomTemplate.streamSources.overlay.title" },
                            { defaultMessage: "Overlay Settings" },
                          )}
                        </Typography>
                        <div className={classes.overlayFields}>
                          <StyledTextField
                            type="number"
                            value={formSource?.scale ?? ""}
                            onChange={handleOverlayFieldChange(index, "scale")}
                            label={intl.formatMessage(
                              { id: "roomTemplate.streamSources.overlay.scale" },
                              { defaultMessage: "Scale" },
                            )}
                            inputProps={{ step: 0.01 }}
                          />
                          <StyledTextField
                            type="number"
                            value={formSource?.posx ?? ""}
                            onChange={handleOverlayFieldChange(index, "posx")}
                            label={intl.formatMessage(
                              { id: "roomTemplate.streamSources.overlay.posx" },
                              { defaultMessage: "PosX" },
                            )}
                            inputProps={{ step: 0.01 }}
                          />
                          <StyledTextField
                            type="number"
                            value={formSource?.posy ?? ""}
                            onChange={handleOverlayFieldChange(index, "posy")}
                            label={intl.formatMessage(
                              { id: "roomTemplate.streamSources.overlay.posy" },
                              { defaultMessage: "PosY" },
                            )}
                            inputProps={{ step: 0.01 }}
                          />
                        </div>
                      </div>
                    )}
                    <Divider className={classes.divider} />

                    <div>
                      <Typography variant="body1" gutterBottom className={classes.urlText}>
                        <strong>
                          {intl.formatMessage({ id: "roomTemplate.streamSources.url" }, { defaultMessage: "URL" })}:
                        </strong>{" "}
                        {formSource?.url || streamSource.url}
                      </Typography>
                    </div>

                    {hasCamOp && formSource?.steerable && (
                      <div className={classes.steerableContainer}>
                        <Typography variant="subtitle2" gutterBottom className={classes.steerableTitle}>
                          {intl.formatMessage(
                            { id: "roomTemplate.streamSources.steerable.title" },
                            { defaultMessage: "Camera Operator Settings" },
                          )}
                        </Typography>
                        <div className={classes.steerableFields}>
                          <StyledTextField
                            type="text"
                            value={formSource.steerable.cfg || ""}
                            onChange={handleSteerableFieldChange(index, "cfg")}
                            label={intl.formatMessage(
                              { id: "roomTemplate.streamSources.steerable.cfg" },
                              { defaultMessage: "Settings file" },
                            )}
                            fullWidth
                            error={!!errors?.file?.stream_sources?.[selectedIndex]?.steerable?.cfg}
                            helperText={errors?.file?.stream_sources?.[selectedIndex]?.steerable?.cfg?.message || ""}
                          />
                          <StyledTextField
                            type="text"
                            value={formSource.steerable.params || ""}
                            onChange={handleSteerableFieldChange(index, "params")}
                            label={intl.formatMessage(
                              { id: "roomTemplate.streamSources.steerable.params" },
                              { defaultMessage: "Params" },
                            )}
                            fullWidth
                          />
                        </div>
                      </div>
                    )}

                    {isChecked && !isVptz && !isAudioSource && (
                      <div className={classes.steerableContainer} style={{ marginTop: "16px" }}>
                        <SimpleCheckbox
                          id={`useAutoExposure-${index}`}
                          name={`useAutoExposure-${index}`}
                          value="useAutoExposure"
                          checked={!!formSource?.useAutoExposure}
                          onChange={handleUseAutoExposureChange(index)}
                          label={intl.formatMessage(
                            { id: "roomTemplate.streamSources.autoExposure.use" },
                            { defaultMessage: "Use Auto Exposure" },
                          )}
                        />
                        {formSource?.useAutoExposure && (
                          <>
                            <Typography variant="subtitle2" gutterBottom className={classes.steerableTitle}>
                              {intl.formatMessage(
                                { id: "roomTemplate.streamSources.autoExposure.title" },
                                { defaultMessage: "Auto Exposure Settings" },
                              )}
                            </Typography>
                            <div className={classes.steerableFields}>
                              <StyledTextField
                                type="text"
                                value={formSource?.exp_camera_url || ""}
                                onChange={handleAutoExposureFieldChange(index, "exp_camera_url")}
                                label={intl.formatMessage(
                                  { id: "roomTemplate.streamSources.autoExposure.expCameraUrl" },
                                  { defaultMessage: "Exposure Camera URL" },
                                )}
                                fullWidth
                                error={!!errors?.file?.stream_sources?.[selectedIndex]?.exp_camera_url}
                                helperText={
                                  errors?.file?.stream_sources?.[selectedIndex]?.exp_camera_url?.message ||
                                  intl.formatMessage(
                                    { id: "roomTemplate.streamSources.autoExposure.expCameraUrl.helper" },
                                    { defaultMessage: "URL for camera auto-exposure control" },
                                  )
                                }
                              />
                              <StyledTextField
                                type="text"
                                value={formSource?.exp_camera_cfg || ""}
                                onChange={handleAutoExposureFieldChange(index, "exp_camera_cfg")}
                                label={intl.formatMessage(
                                  { id: "roomTemplate.streamSources.autoExposure.expCameraCfg" },
                                  { defaultMessage: "Exposure Config File" },
                                )}
                                fullWidth
                                error={!!errors?.file?.stream_sources?.[selectedIndex]?.exp_camera_cfg}
                                helperText={
                                  errors?.file?.stream_sources?.[selectedIndex]?.exp_camera_cfg?.message ||
                                  intl.formatMessage(
                                    { id: "roomTemplate.streamSources.autoExposure.expCameraCfg.helper" },
                                    {
                                      defaultMessage:
                                        "Configuration file for auto-exposure (e.g., auto_expo_config.json)",
                                    },
                                  )
                                }
                              />
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    <div>
                      <div className={classes.urlParametersHeader}>
                        <Typography variant="subtitle2">
                          {intl.formatMessage(
                            { id: "roomTemplate.streamSources.urlParameters" },
                            { defaultMessage: "URL Parameters" },
                          )}
                        </Typography>
                        <Button
                          size="small"
                          color="primary"
                          startIcon={<AddIcon />}
                          onClick={handleAddUrlParameter(index)}
                        >
                          {intl.formatMessage(
                            { id: "roomTemplate.streamSources.addParameter" },
                            { defaultMessage: "Add Parameter" },
                          )}
                        </Button>
                      </div>

                      {getUrlParameters(index).length > 0 ? (
                        <div className={classes.urlParametersGrid}>
                          {getUrlParameters(index).map((param, paramIndex) => (
                            <div key={paramIndex} className={classes.parameterItem}>
                              <div className={classes.parameterFields}>
                                <StyledTextField
                                  type="text"
                                  value={param.key || ""}
                                  onChange={handleUrlParameterChange(index, paramIndex, "key")}
                                  label={intl.formatMessage(
                                    { id: "roomTemplate.streamSources.parameterKey" },
                                    { defaultMessage: "Key" },
                                  )}
                                  className={classes.parameterField}
                                  disabled={
                                    (streamSource.brand === "Axis" && param.key === "audio") ||
                                    (isVptz && param.key === "dewarp_id")
                                  }
                                />
                                <StyledTextField
                                  type="text"
                                  value={param.value || ""}
                                  onChange={handleUrlParameterChange(index, paramIndex, "value")}
                                  label={intl.formatMessage(
                                    { id: "roomTemplate.streamSources.parameterValue" },
                                    { defaultMessage: "Value" },
                                  )}
                                  className={classes.parameterField}
                                  disabled={streamSource.brand === "Axis" && param.key === "audio"}
                                  error={isVptz && param.key === "dewarp_id" && !!errors?.file?.stream_sources?.[selectedIndex]?.url}
                                  helperText={
                                    isVptz && param.key === "dewarp_id" && errors?.file?.stream_sources?.[selectedIndex]?.url?.message
                                      ? errors.file.stream_sources[selectedIndex].url.message
                                      : ""
                                  }
                                />
                              </div>
                              <IconButton
                                color="secondary"
                                onClick={handleRemoveUrlParameter(index, paramIndex)}
                                size="small"
                                aria-label="remove parameter"
                                className={classes.steerableExpSection}
                                disabled={
                                  (streamSource.brand === "Axis" && param.key === "audio") ||
                                  (isVptz && param.key === "dewarp_id")
                                }
                              >
                                <DeleteIcon />
                              </IconButton>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          {intl.formatMessage(
                            { id: "roomTemplate.streamSources.noParameters" },
                            { defaultMessage: "No parameters added yet" },
                          )}
                        </Typography>
                      )}
                    </div>

                    <Divider className={classes.dividerSpacing} />
                  </div>
                </AccordionDetails>
              </Accordion>
            );
          })
        ) : (
          <Typography variant="body2" color="textSecondary">
            {intl.formatMessage(
              { id: "roomTemplate.streamSources.noSources" },
              { defaultMessage: "No stream sources available" },
            )}
          </Typography>
        )}
      </DataGridItem>
    </>
  );
};

StreamSourcesSection.propTypes = {
  intl: PropTypes.object.isRequired,
  allStreamSources: PropTypes.array.isRequired,
  values: PropTypes.object.isRequired,
  errors: PropTypes.object,
  selectedStreamSources: PropTypes.array.isRequired,
  setSelectedStreamSources: PropTypes.func.isRequired,
  camOpSourceIndex: PropTypes.number,
  expandedAccordions: PropTypes.object.isRequired,
  setExpandedAccordions: PropTypes.func.isRequired,
  handleStreamSourceSelectionChange: PropTypes.func.isRequired,
  handleStreamSourceLabelChange: PropTypes.func.isRequired,
  handleCamOpChange: PropTypes.func.isRequired,
  handleAddAudioSource: PropTypes.func.isRequired,
  handleRemoveAudioSource: PropTypes.func.isRequired,
  audioSourceLinks: PropTypes.object.isRequired,
  handlePreselectedChange: PropTypes.func.isRequired,
  handleMixerVideoChange: PropTypes.func.isRequired,
  handleStreamSourceTypeChange: PropTypes.func.isRequired,
  handleSteerableFieldChange: PropTypes.func.isRequired,
  handleAutoExposureFieldChange: PropTypes.func.isRequired,
  handleAddUrlParameter: PropTypes.func.isRequired,
  getUrlParameters: PropTypes.func.isRequired,
  handleUrlParameterChange: PropTypes.func.isRequired,
  handleRemoveUrlParameter: PropTypes.func.isRequired,
  setAllStreamSources: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  sourceFormData: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
};

export default injectIntl(withStyles(styles)(StreamSourcesSection));
